import { AnyAction } from '@reduxjs/toolkit';
import {
  SIGN_IN,
  SIGN_UP,
  SIGN_OUT,
  SIGN_UP_UPDATE,
  SIGN_IN_UPDATE,
} from '../../constants/actionTypes';
import {
  serializeForRedux,
  containsFirestoreTimestampLike,
} from '@src/utilities/serializeForRedux';

export interface UserData {
  email?: string;
  emailVerified?: boolean;
  isAnonymous?: boolean;
  phoneNumber?: string;
  accessToken?: string;
  uid?: string;
  [key: string]: unknown;
}

export interface UserState {
  user: UserData;
}

const storedUserRaw =
  (typeof localStorage !== 'undefined' &&
    localStorage.getItem('userData') &&
    JSON.parse(localStorage.getItem('userData')!)) ||
  {};

const initialState: UserState = {
  user: serializeForRedux(storedUserRaw as UserData),
};

const persistUser = (user: UserData): void => {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('userData', JSON.stringify(user));
  }
};

export default function userReducers(
  state: UserState = initialState,
  action: AnyAction
): UserState {
  const payload = action.payload as UserData & { data?: Record<string, unknown> } | undefined;
  const userData: UserData = state?.user?.accessToken
    ? { ...state.user }
    : {
        email: payload?.email,
        emailVerified: payload?.emailVerified,
        isAnonymous: payload?.isAnonymous,
        phoneNumber: payload?.phoneNumber,
        accessToken: payload?.accessToken,
        uid: payload?.uid,
        ...payload?.data,
      };

  switch (action.type) {
    case SIGN_IN: {
      const user = serializeForRedux(userData);
      persistUser(user);
      return {
        ...state,
        user,
      };
    }
    case SIGN_IN_UPDATE: {
      const user = serializeForRedux((payload as UserData) ?? state.user);
      if (payload !== undefined) {
        persistUser(user);
      }
      return {
        ...state,
        user,
      };
    }
    case SIGN_UP: {
      const user = serializeForRedux(userData);
      persistUser(user);
      return {
        ...state,
        user,
      };
    }
    case SIGN_UP_UPDATE: {
      const user = serializeForRedux((payload as UserData) ?? state.user);
      if (payload !== undefined) {
        persistUser(user);
      }
      return {
        ...state,
        user,
      };
    }
    case SIGN_OUT: {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('userData', '');
        localStorage.setItem('RESTAURANTS_DATA', '');
        localStorage.setItem('REACT_APP_VERSION', '');
        localStorage.setItem('REACT_APP_FORCE_ACTIONS', '');
        localStorage.setItem('SELECTED_RESTAURANT', '');
        localStorage.setItem('GLOBAL_CONFIG', '');
      }
      return {
        ...state,
        user: {},
      };
    }
    default: {
      // Other actions (e.g. ADD_CART_DATA) still run serializable checks on full state.
      // Repair stale sessions that still hold live Firestore Timestamp instances.
      if (!containsFirestoreTimestampLike(state.user)) {
        return state;
      }
      const user = serializeForRedux(state.user);
      persistUser(user);
      return { ...state, user };
    }
  }
}
